import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { latinDealer } from "../Shared/Generation/latinDealer.ts";
import { formatScaled, rescale, trailingPlaces } from "../Shared/scaledDecimal.ts";
import {
    Fraction,
    IntegerQuestionOptions,
    ProblemData,
    RationalFractionProblem,
    RationalKindOptions,
    SignedFraction,
} from "./Constants.ts";
import { greatestCommonDivisor, toMixedNumber } from "./FractionGenerator.ts";
import { ISettingsProps } from "./Models.ts";

/**
 * Operations on rational numbers: negative decimals and signed fractions, as new
 * question types on the Integers format. See ADR 0122.
 *
 * It sits beside `IntegerGenerator` rather than in it because almost nothing is
 * shared with the whole-number questions beyond the notation (ADR 0030 §4): a
 * leading negative is bare, a negative after an operator is in parentheses, and
 * both signs are the ASCII `-`.
 *
 * **Decimals** are scaled integers throughout (`Shared/scaledDecimal.ts`), and
 * nothing passes through a float. **Fractions** are signed integer numerators
 * over positive denominators, reduced with the same `toMixedNumber` the
 * Fractions format uses. Each problem is all one kind, never `-0.5 + 3/4`.
 *
 * **Signs.** Every problem has a negative operand and neither is zero; the
 * three patterns that allows (-,+), (+,-) and (-,-) are equally likely.
 *
 * **Dealing.** A page holds its operations, and its two kinds when the setting
 * is Both, in equal shares as near as the count allows - in each of the two
 * directions at once (see `dealer`), because the operations are what the
 * standards split on and the kinds are what the setting names.
 */

export type RationalOperator = "+" | "-" | "×" | "÷";
type RationalNumberKind = "decimal" | "fraction";

export interface RationalChoice {
    operator: RationalOperator;
    kind: RationalNumberKind;
}

/** Which operations a question type asks (ADR 0122 §1). */
export function operatorsFor(question: IntegerQuestionOptions): RationalOperator[] {
    switch (question) {
        case IntegerQuestionOptions.RationalAddSubtract:
            return ["+", "-"];
        case IntegerQuestionOptions.RationalMultiplyDivide:
            return ["×", "÷"];
        default:
            return ["+", "-", "×", "÷"];
    }
}

/** Which kinds of number a setting asks. */
export function kindsFor(kind: RationalKindOptions | undefined): RationalNumberKind[] {
    switch (kind) {
        case RationalKindOptions.Decimals:
            return ["decimal"];
        case RationalKindOptions.Fractions:
            return ["fraction"];
        default:
            return ["decimal", "fraction"];
    }
}

/**
 * One page's choices, dealt evenly by operation and by kind at once.
 *
 * A fair shuffle of the crossed list (what `questionSequence` does) gives every
 * choice `floor` or `ceil` of its share, but it can hand the extras to two
 * choices that share an operation, so a page of ten could hold four additions
 * and two of everything else. Here the crossed list is laid out as a Latin
 * square - round `r` pairs operation `i` with kind `(i + r) mod kinds` - so any
 * prefix of it is balanced in both directions, and the extras are a prefix. The
 * operations and kinds are shuffled first so *which* get the extra is still
 * chosen by the seed rather than fixed.
 */
export function dealer(
    operators: RationalOperator[],
    kinds: RationalNumberKind[]
): (count: number) => RationalChoice[] {
    const deal = latinDealer(operators, kinds);
    return (count) => deal(count).map(({ row, column }) => ({ operator: row, kind: column }));
}

/**
 * Which operands are negative. The three patterns - negative then positive,
 * positive then negative, both negative - are equally likely; two positives is
 * a fifth-grade question with nothing to say about signs (ADR 0122 §3).
 */
function drawSigns(): [boolean, boolean] {
    const pattern = getRandomInt(0, 2);
    return [pattern !== 1, pattern !== 0];
}

/** How a signed operand is written (ADR 0030 §4): bare when leading, bracketed after an operator. */
function signed(text: string, negative: boolean, leading: boolean): string {
    if (!negative) {
        return text;
    }
    return leading ? `-${text}` : `(-${text})`;
}

// ─── decimals ───────────────────────────────────────────────────────────────

/** `scaled` at `places`, in its shortest exact form: -1.5, -0.25, -2. */
function shortest(scaled: number, places: number): string {
    const kept = trailingPlaces(scaled, places);
    return formatScaled(rescale(scaled, places, kept), kept);
}

/**
 * A positive decimal at exactly `places` places, whose last place is not zero
 * (no `4.50`, no `3.0`), with a whole part below `dial`. Built from its digits
 * rather than drawn and rejected, so it takes the same number of draws every time.
 */
function decimalMagnitude(places: number, dial: number): number {
    const head = getRandomInt(0, dial * 10 ** (places - 1) - 1);
    return head * 10 + getRandomInt(1, 9);
}

function decimalProblem(
    key: number,
    question: IntegerQuestionOptions,
    prompt: string,
    answer: string
): ProblemData {
    return { key, numbers: [], operator: "", display: [prompt, answer], integerQuestion: question };
}

function buildDecimalAddSubtract(
    key: number,
    question: IntegerQuestionOptions,
    operator: "+" | "-",
    dial: number
): ProblemData | null {
    // Both operands at the same place count, so the points would line up.
    const places = getRandomInt(1, 2);
    const [firstNegative, secondNegative] = drawSigns();
    const first = decimalMagnitude(places, dial);
    const second = decimalMagnitude(places, dial);
    const left = firstNegative ? -first : first;
    const right = secondNegative ? -second : second;
    const result = operator === "+" ? left + right : left - right;
    if (result === 0) {
        // -2.5 + 2.5: a zero answer says nothing about signs.
        return null;
    }

    return decimalProblem(
        key,
        question,
        `${signed(formatScaled(first, places), firstNegative, true)} ${operator} ${signed(formatScaled(second, places), secondNegative, false)}`,
        `${result < 0 ? "-" : ""}${shortest(Math.abs(result), places)}`
    );
}

/** A 1-place decimal below 10 whose last place is not zero: 0.5, 3.6, 9.9. */
function smallDecimal(): number {
    return getRandomInt(0, 9) * 10 + getRandomInt(1, 9);
}

function buildDecimalMultiply(
    key: number,
    question: IntegerQuestionOptions,
    dial: number
): ProblemData {
    const [firstNegative, secondNegative] = drawSigns();
    const first = decimalMagnitude(1, dial);

    // The two shapes ADR 0004 offers: by a whole number, or by a decimal.
    const byWhole = getRandomInt(0, 1) === 0;
    const second = byWhole ? getRandomInt(2, 9) : smallDecimal();
    const secondPlaces = byWhole ? 0 : 1;
    const placesOfProduct = 1 + secondPlaces;

    return decimalProblem(
        key,
        question,
        `${signed(formatScaled(first, 1), firstNegative, true)} × ${signed(formatScaled(second, secondPlaces), secondNegative, false)}`,
        `${firstNegative !== secondNegative ? "-" : ""}${shortest(first * second, placesOfProduct)}`
    );
}

/**
 * Built backwards, as 0004 and 0030 build it: a quotient and a divisor are drawn
 * and the dividend is their product, so the division is exact and never repeats.
 * The dividend is what a student reads first, so it must not be a whole number
 * dressed as a decimal (`10.0`): a draw that lands on one is drawn again.
 */
function buildDecimalDivide(
    key: number,
    question: IntegerQuestionOptions,
    dial: number
): ProblemData | null {
    const [firstNegative, secondNegative] = drawSigns();
    const byWhole = getRandomInt(0, 1) === 0;
    const divisorPlaces = byWhole ? 0 : 1;
    const dividendPlaces = 1 + divisorPlaces;

    for (let attempt = 0; attempt < 50; attempt++) {
        const divisor = byWhole ? getRandomInt(2, 9) : smallDecimal();
        // A 1-place quotient up to the dial. With a decimal divisor it may be a
        // whole number (6 / 0.3 = 20, answered `-20`); the answer prints shortest.
        const quotient = getRandomInt(1, dial * 10);
        const dividend = quotient * divisor;
        if (trailingPlaces(dividend, dividendPlaces) === 0) {
            continue;
        }
        return decimalProblem(
            key,
            question,
            `${signed(shortest(dividend, dividendPlaces), firstNegative, true)} ÷ ${signed(formatScaled(divisor, divisorPlaces), secondNegative, false)}`,
            `${firstNegative !== secondNegative ? "-" : ""}${shortest(quotient, 1)}`
        );
    }
    return null;
}

// ─── fractions ──────────────────────────────────────────────────────────────

const LARGEST_DENOMINATOR = 12;

/** A proper fraction in lowest terms over `denominator`. */
function properFraction(denominator: number): Fraction {
    const numerators: number[] = [];
    for (let n = 1; n < denominator; n++) {
        if (greatestCommonDivisor(n, denominator) === 1) {
            numerators.push(n);
        }
    }
    return { numerator: numerators[getRandomInt(0, numerators.length - 1)], denominator };
}

/** Two denominators from 2 to 12, like or unlike at random (ADR 0122 §5). */
function denominatorPair(): [number, number] {
    const first = getRandomInt(2, LARGEST_DENOMINATOR);
    if (getRandomInt(0, 1) === 0) {
        return [first, first];
    }
    // One of the other ten: a draw that skips `first` without redrawing.
    const draw = getRandomInt(2, LARGEST_DENOMINATOR - 1);
    return [first, draw >= first ? draw + 1 : draw];
}

/** `-3/4`, `(-3/4)` or `3/4`: the readable inline form kept in `display`. */
function inlineFraction(fraction: SignedFraction, leading: boolean): string {
    return signed(`${fraction.numerator}/${fraction.denominator}`, fraction.negative, leading);
}

/** `-1 1/4`, `-5/12`, `2`: the sign in front of the whole mixed number. */
export function inlineAnswer(fraction: SignedFraction): string {
    const parts: string[] = [];
    if (fraction.whole !== undefined && fraction.whole !== 0) {
        parts.push(String(fraction.whole));
    }
    if (fraction.numerator !== 0) {
        parts.push(`${fraction.numerator}/${fraction.denominator}`);
    }
    return `${fraction.negative ? "-" : ""}${parts.join(" ") || "0"}`;
}

/** `toMixedNumber`'s signed result, split into a magnitude and a sign. */
function signedFractionOf(mixed: Fraction): SignedFraction {
    const negative = (mixed.whole ?? mixed.numerator) < 0;
    const answer: SignedFraction = {
        negative,
        numerator: Math.abs(mixed.numerator),
        denominator: mixed.denominator,
    };
    if (mixed.whole !== undefined) {
        answer.whole = Math.abs(mixed.whole);
    }
    return answer;
}

function buildFractionProblem(
    key: number,
    question: IntegerQuestionOptions,
    operator: RationalOperator
): ProblemData | null {
    const [firstDenominator, secondDenominator] = denominatorPair();
    const [firstNegative, secondNegative] = drawSigns();
    const first = properFraction(firstDenominator);
    const second = properFraction(secondDenominator);
    const a = firstNegative ? -first.numerator : first.numerator;
    const b = secondNegative ? -second.numerator : second.numerator;

    let numerator: number;
    let denominator: number;
    switch (operator) {
        case "+":
            numerator = a * secondDenominator + b * firstDenominator;
            denominator = firstDenominator * secondDenominator;
            break;
        case "-":
            numerator = a * secondDenominator - b * firstDenominator;
            denominator = firstDenominator * secondDenominator;
            break;
        case "×":
            numerator = a * b;
            denominator = firstDenominator * secondDenominator;
            break;
        default:
            // The reciprocal: the divisor's sign moves to the denominator.
            numerator = a * secondDenominator;
            denominator = firstDenominator * b;
            break;
    }
    if (numerator === 0) {
        return null;
    }

    const operands: [SignedFraction, SignedFraction] = [
        { negative: firstNegative, ...first },
        { negative: secondNegative, ...second },
    ];
    const answer = signedFractionOf(toMixedNumber(numerator, denominator));
    const spec: RationalFractionProblem = { operands, operator, answer };

    return {
        key,
        numbers: [],
        operator: "",
        // For the places that read text (the on-screen cards); the sheet draws
        // `rationalFractions` as stacked fractions.
        display: [
            `${inlineFraction(operands[0], true)} ${operator} ${inlineFraction(operands[1], false)}`,
            inlineAnswer(answer),
        ],
        integerQuestion: question,
        rationalFractions: spec,
    };
}

// ─── the sheet ──────────────────────────────────────────────────────────────

/** One problem of a rational question type. */
export function buildRationalProblem(
    question: IntegerQuestionOptions,
    choice: RationalChoice,
    dial: number,
    key: number
): ProblemData | null {
    if (choice.kind === "fraction") {
        return buildFractionProblem(key, question, choice.operator);
    }
    switch (choice.operator) {
        case "×":
            return buildDecimalMultiply(key, question, dial);
        case "÷":
            return buildDecimalDivide(key, question, dial);
        default:
            return buildDecimalAddSubtract(key, question, choice.operator, dial);
    }
}

/** A page-shaped set of rational problems, dealt evenly by operation and kind. */
export function generateRationalPages(settings: ISettingsProps, dial: number): ProblemData[][] | null {
    const question = settings.IntegerQuestion;
    const operators = operatorsFor(question);
    const kinds = kindsFor(settings.RationalKind);
    const choices: RationalChoice[] = operators.flatMap((operator) => kinds.map((kind) => ({ operator, kind })));

    return generateProblemPages({
        settings,
        choices,
        dealPage: dealer(operators, kinds),
        build: (choice, key) => buildRationalProblem(question, choice, dial, key),
        fingerprint: (problem) => problem.display![0],
    });
}
